import { FC, useCallback, useState } from 'react'
import styled from 'styled-components'
import UploadItem from 'components/Pages/Review/Common/Form/Content/Product/RecognitionModal/Form/LabelsImages/UploadItem'
import { Trans, useTranslation } from 'react-i18next'
import { uploadFile } from 'utils/uploadFile'
import ErrorMessage from 'components/Common/MasterComponents/ErrorMessage'
import { validateFile } from 'components/Pages/SellWine/AddNewOffer/V2/Create/Wine/Left/ByImage/Common/_methods'
import { Controller, useFormContext } from 'react-hook-form'
import ImagesContainer from '../ImagesContainer'

const Description = styled.div`
  margin-top: 8px;
  font: var(--typography-12-16-normal);
  color: var(--color-sw-60);
`
interface BottleImagesProps {
  bottleImageTitle?: string
}
// Компонент используется и в Common/Modals/RecognizeWine
const BottleImages: FC<BottleImagesProps> = ({
  bottleImageTitle = 'capturedWines.dialog.updateImages.bottle',
}) => {
  const [loadingStates, setLoadingStates] = useState({
    frontLabel: false,
    backLabel: false,
    bottleLabel: false,
  })
  const { t } = useTranslation()
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useFormContext()
  const bottleImagesErrors = errors.bottleImages as any
  const handleFileUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      throw new Error(validationError)
    }
    return await uploadFile(file)
  }, [])

  const createHandleChange = useCallback(
    (field: string, onBlur: () => void) =>
      async (file: File | FileList | null) => {
        if (!file) {
          setValue(`bottleImages.${field}`, null, { shouldDirty: true })
          onBlur()
          return
        }

        // Handle FileList
        if (file instanceof FileList) {
          if (file[0]) {
            file = file[0]
          } else {
            return
          }
        }

        setLoadingStates(prev => ({ ...prev, [field]: true }))

        try {
          const uploadResult = await handleFileUpload(file)
          setValue(`bottleImages.${field}`, uploadResult, { shouldDirty: true })
          clearErrors(`bottleImages.${field}`)
          onBlur()
        } catch (error: any) {
          setValue(`bottleImages.${field}`, null, { shouldDirty: true })
          setError(`bottleImages.${field}`, {
            type: 'manual',
            message: error?.message,
          })
        } finally {
          setLoadingStates(prev => ({ ...prev, [field]: false }))
        }
      },
    [handleFileUpload, setError, clearErrors, setValue]
  )

  const firstError =
    bottleImagesErrors &&
    (Object.values(bottleImagesErrors).find(
      (error: any) => error?.message
    ) as any)

  return (
    <ImagesContainer
      imagesNode={
        <>
          <Controller
            name='bottleImages.frontLabel'
            control={control}
            rules={{ required: t('error.fieldRequired') }}
            render={({ field: { value, onBlur } }) => (
              <UploadItem
                title={`${t('capturedWines.dialog.updateImages.front')}*`}
                value={value || null}
                onChange={createHandleChange('frontLabel', onBlur)}
                hasError={!!bottleImagesErrors?.frontLabel}
                loading={loadingStates.frontLabel}
              />
            )}
          />
          <Controller
            name='bottleImages.backLabel'
            control={control}
            rules={{ required: t('error.fieldRequired') }}
            render={({ field: { value, onBlur } }) => (
              <UploadItem
                title={t('capturedWines.dialog.updateImages.back') + '*'}
                value={value || null}
                onChange={createHandleChange('backLabel', onBlur)}
                hasError={!!bottleImagesErrors?.backLabel}
                loading={loadingStates.backLabel}
              />
            )}
          />
          <Controller
            name='bottleImages.bottleLabel'
            control={control}
            render={({ field: { value, onBlur } }) => (
              <UploadItem
                title={t(bottleImageTitle)}
                value={value || null}
                onChange={createHandleChange('bottleLabel', onBlur)}
                hasError={!!bottleImagesErrors?.bottleLabel}
                loading={loadingStates.bottleLabel}
              />
            )}
          />
        </>
      }
      descriptionNode={
        <Description>
          <Trans t={t} i18nKey='capturedWines.dialog.updateImages.formats' />
        </Description>
      }
      errorNode={
        firstError && <ErrorMessage>{firstError.message}</ErrorMessage>
      }
    />
  )
}

export default BottleImages
